import React, { useCallback, useState, useMemo, useEffect } from 'react';

import debounce from 'lodash/debounce';
import { FieldProps, useField, FieldRenderProps } from 'react-final-form';

import { RadioInput as Input } from '@vk-hr-tek/ui/input';

import { compose, ValidateType } from '../validation';

/**
 * Компонент радио-кнопок.
 * Оборачивает компонент RadioInput из модуля input в поле формы react-final-form.
 *
 * @param {Object} props - Свойства компонента.
 * @param {string} [props.label] - Подпись для группы радио-кнопок.
 * @param {string} [props.tooltip] - Текст подсказки, отображаемый при наведении на радио-кнопки.
 * @param {string} props.name - Уникальное имя поля ввода.
 * @param {function} [props.onChange] - Пользовательский обработчик изменения значения.
 * @param {Array} props.options - Массив опций для радио-кнопок.
 * @param {string | number} props.options[].value - Значение опции.
 * @param {string} props.options[].label - Подпись опции.
 * @param {boolean} [props.options[].disabled] - Флаг, указывающий, должна ли опция быть отключена.
 * @param {string} [props.defaultValue] - Значение по умолчанию для поля ввода.
 * @param {boolean} [props.required] - Флаг, указывающий, является ли поле обязательным.
 * @param {boolean} [props.disabled] - Флаг, указывающий, должно ли поле быть отключено.
 * @param {boolean} [props.row=false] - Определяет, должны ли радиокнопки отображаться в ряд.
 * @param {function} [props.after] - Функция, возвращающая дополнительный контент после группы радио-кнопок.
 * @returns {React.ReactNode} - Возвращает группу радио-кнопок, обернутую в поле формы.
 */
const RadioInput = ({
  label,
  tooltip,
  name,
  onChange,
  options,
  defaultValue,
  autoSetDefault,
  required,
  disabled,
  row,
  withoutMargins = false,
  validate,
  debounceTimeout,
  ...rest
}: {
  defaultValue?: string;
  validate?: (ValidateType | undefined)[] | ValidateType;
  autoSetDefault?: boolean;
  options: {
    value: string | number;
    label: string;
    disabled?: boolean;
    tooltip?: string;
  }[];
  after?: (value: string | undefined) => React.ReactNode;
  debounceTimeout?: number;
} & FieldProps<string | undefined, FieldRenderProps<string | undefined>>) => {
  const { input } = useField(name, {
    ...rest,
    validate: Array.isArray(validate) ? compose(validate) : validate,
    initialValue: defaultValue,
  });

  const { onChange: onInputChange } = input;

  const [value, setValue] = useState<string | number | undefined>(input.value);

  const handleChangeForm = useCallback(
    (formValue: string | number) => {
      onInputChange(formValue);

      if (onChange) {
        onChange(formValue);
      }
    },
    [onInputChange, onChange],
  );

  const handleChangeDebounce = useMemo(
    () => debounce(handleChangeForm, debounceTimeout),
    [debounceTimeout, handleChangeForm],
  );

  useEffect(() => {
    if (autoSetDefault && !input.value) {
      input.onChange(defaultValue || options[0].value);
    }
  }, [autoSetDefault, defaultValue, input, options]);

  const handleChange = useCallback(
    (changedValue: string | number) => {
      setValue(changedValue);

      if (debounceTimeout) {
        handleChangeDebounce(changedValue);
      } else {
        handleChangeForm(changedValue);
      }
    },
    [debounceTimeout, handleChangeDebounce, handleChangeForm],
  );

  if (!options || !options.length) {
    return null;
  }

  return (
    <Input
      {...input}
      items={options}
      label={label}
      value={value || input.value}
      onChange={handleChange}
      required={required}
      disabled={disabled}
      tooltip={tooltip}
      row={row}
      withoutMargins={withoutMargins}
    />
  );
};

export default RadioInput;
